import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Link,
  Text,
  VStack
} from "@chakra-ui/react";
import { GETLicensePayload, UserLicensesEndpoints } from "./utils";
import {
  MdAccountBalanceWallet,
  MdInfoOutline,
  MdRemove
} from "react-icons/md";
import { useEffect, useState } from "react";
import { useApplicationContext } from "contexts/ApplicationContext";
import { usePromiseData } from "hooks/use-promise-data";
import {
  CarteiraEndpoints,
  getWalletBalanceFormatted
} from "components/carteira-components/utils";
import { FaUserTie } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { Toast } from "components/toast";
import {
  ModalUpdateChanges,
  openModalUpdateChanges
} from "./modal-update-changes";
import {
  ModalAdicionarSaldoStorm,
  openModalAdicionarSaldoStorm
} from "components/carteira-components/modal-adicionar-saldo-storm";
import {
  ModalAdicionarSaldoPIX,
  openModalAdicionarSaldoPIX
} from "components/carteira-components/modal-adicionar-saldo-pix";
import { PIXIcon } from "components/vectors/pix-icon";
import { CashIcon } from "components/vectors/cash-icon";
import { getToken } from "Session";
import { autoLogin } from "contexts/attendance-context/utils";
import { useRouter } from "router-manager/parts";

// Constants
const MASTER_PRICE_NUM = 99.9;
const VO_PRICE_NUM = 9.9;
const MASTER_PRICE = "R$ 99,90";
const VO_PRICE = "R$ 9,90";
const migrationBillingStart = "02/03/2026";

// Formatting function
const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

export function ConfirmLicense({
  licenseData,
  onUpdateLicenses
}: {
  licenseData: Partial<GETLicensePayload>;
  onUpdateLicenses: (data?: GETLicensePayload) => void;
}) {
  const { user, setUserAction } = useApplicationContext();
  const customerId = user.userData.customerId;
  const { result: carteiraData, onUpdate } = usePromiseData(() =>
    CarteiraEndpoints.getSaldoCarteira(customerId!)
  );
  const [masterLicensesToConfirm, setMasterLicensesToConfirm] = useState(0);
  const [voLicensesToConfirm, setVoLicensesToConfirm] = useState(0);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  // Limits
  const MAX_MASTER = Math.max(
    licenseData.qtyEnabledMaster || 0,
    licenseData.qtyLicenseMaster || 0
  );
  const MAX_VO = 200;
  const MIN_MASTER = 1;
  const MIN_VO = 0;

  useEffect(() => {
    setMasterLicensesToConfirm(licenseData.qtyEnabledMaster || 0);
    setVoLicensesToConfirm(licenseData.qtyEnabledVendedorOrOperacional || 0);
  }, [
    licenseData.qtyEnabledMaster,
    licenseData.qtyEnabledVendedorOrOperacional
  ]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      onUpdate();
    }, 3000);

    return () => clearInterval(intervalId);
  }, [onUpdate]);

  // Calculations
  const newMonthlyTotal =
    masterLicensesToConfirm * MASTER_PRICE_NUM +
    voLicensesToConfirm * VO_PRICE_NUM;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const data = await UserLicensesEndpoints.putLicense(
        user.userData.customerId!,
        {
          qtyLicenseMaster: masterLicensesToConfirm,
          qtyLicenseVendedorOrOperacional: voLicensesToConfirm
        }
      );
      const thisRouter =
        window.location.pathname !== "/login" ? window.location.pathname : null;

      await autoLogin({ setUserAction, router, thisRouter });

      onUpdateLicenses(data);
    } catch (e: any) {
      const errorMessage =
        e?.data?.message === "sem_saldo"
          ? "Saldo insuficiente"
          : "Erro desconhecido";

      Toast({
        title: "Erro ao atualizar licença",
        description: errorMessage,
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalUpdateChanges />
      <Box as="main" flex="1" maxW="2xl" w="full" mx="auto" px="4" py="12">
        <VStack align="center" mb="6">
          <Flex
            align="center"
            justify="center"
            w="16"
            h="16"
            rounded="full"
            bg="orange.100"
            color="orange.700"
          >
            <Icon as={MdInfoOutline} w="8" h="8" />
          </Flex>
          <Heading fontSize="3xl" color="#1B2559">
            Confirme sua licença
          </Heading>
        </VStack>

        <Box
          p="4"
          w="100%"
          mt="4"
          mb="6"
          position="relative"
          overflow="hidden"
          bg="white"
          border="1px"
          borderColor="gray.200"
          rounded="xl"
          shadow="sm"
        >
          <Box
            position="absolute"
            left="0"
            top="0"
            bottom="0"
            w="4px"
            bg="primary.400"
          />
          <Flex gap="2">
            <Box color="var(--chakra-colors-primary-400)" pt="0px">
              <Icon as={MdInfoOutline} w="5" h="5" />
            </Box>
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="gray.800" mb="1">
                Como funciona a cobrança?
              </Text>
              <Text fontSize="sm" color="gray.600" lineHeight="tall">
                O PowerHub é flexível e cobrado por usuário. A sua assinatura
                requer 1 acesso{" "}
                <Text as="strong" color="gray.800">
                  Master
                </Text>{" "}
                (o seu). Escolha abaixo quantos acessos de{" "}
                <Text as="strong" color="gray.800">
                  Vendedor
                </Text>{" "}
                ou
                <Text as="strong" color="gray.800">
                  {" "}
                  Operacional
                </Text>{" "}
                você precisará para a sua equipe. O valor total será cobrado
                mensalmente.
              </Text>
            </Box>
          </Flex>
        </Box>

        <Box
          bg="white"
          border="1px"
          borderColor="gray.200"
          rounded="xl"
          shadow="sm"
          overflow="hidden"
        >
          <Box p="6">
            <VStack spacing="5" align="stretch">
              {/* Current licenses summary */}
              <VStack spacing="3" align="stretch">
                <HStack
                  justify="space-between"
                  p="4"
                  bg="gray.50"
                  rounded="lg"
                  border="1px"
                  borderColor="gray.100"
                >
                  <HStack spacing="3">
                    <Center p="2" bg="blue.50" color="blue.600" rounded="lg">
                      <Icon
                        as={FaUserTie}
                        w={5}
                        h={5}
                        color="var(--chakra-colors-blue-600)"
                      />
                    </Center>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.900">
                        Master
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {MASTER_PRICE}/mês cada
                      </Text>
                    </Box>
                  </HStack>
                  <Grid
                    gap="4px"
                    alignItems="center"
                    gridTemplateColumns="auto 30px auto"
                  >
                    <IconButton
                      aria-label=""
                      onClick={() =>
                        setMasterLicensesToConfirm((prev) =>
                          Math.max(MIN_MASTER, prev - 1)
                        )
                      }
                      variant="ghost"
                      icon={<>-</>}
                      color="gray.500"
                      isDisabled={masterLicensesToConfirm <= MIN_MASTER}
                    ></IconButton>
                    <Center fontSize="lg" fontWeight="bold" color="gray.900">
                      {masterLicensesToConfirm}
                    </Center>
                    <IconButton
                      aria-label=""
                      onClick={() =>
                        setMasterLicensesToConfirm((prev) =>
                          Math.min(MAX_MASTER, prev + 1)
                        )
                      }
                      variant="ghost"
                      icon={<>+</>}
                      color="gray.500"
                      isDisabled={masterLicensesToConfirm >= MAX_MASTER}
                    ></IconButton>
                  </Grid>
                </HStack>

                <HStack
                  justify="space-between"
                  p="4"
                  bg="gray.50"
                  rounded="lg"
                  border="1px"
                  borderColor="gray.100"
                >
                  <HStack spacing="3">
                    <Center p="2" bg="blue.50" color="blue.600" rounded="lg">
                      <Icon as={FaUser} w={5} h={5} />
                    </Center>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.900">
                        Vendedor / Operacional
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {VO_PRICE}/mês cada
                      </Text>
                    </Box>
                  </HStack>
                  <Grid
                    gap="4px"
                    alignItems="center"
                    gridTemplateColumns="auto 30px auto"
                  >
                    <IconButton
                      aria-label=""
                      onClick={() =>
                        setVoLicensesToConfirm((prev) =>
                          Math.max(MIN_VO, prev - 1)
                        )
                      }
                      variant="ghost"
                      icon={<>-</>}
                      color="gray.500"
                      isDisabled={voLicensesToConfirm <= MIN_VO}
                    >
                      <Icon as={MdRemove} />
                    </IconButton>
                    <Center fontSize="lg" fontWeight="bold" color="gray.900">
                      {voLicensesToConfirm}
                    </Center>
                    <IconButton
                      aria-label=""
                      onClick={() =>
                        setVoLicensesToConfirm((prev) =>
                          Math.min(MAX_VO, prev + 1)
                        )
                      }
                      variant="ghost"
                      icon={<>+</>}
                      color="gray.500"
                      isDisabled={voLicensesToConfirm >= MAX_VO}
                    ></IconButton>
                  </Grid>
                </HStack>
              </VStack>

              {/* Monthly Total */}
              <Box
                bg="gray.50"
                border="1px"
                borderColor="gray.200"
                rounded="lg"
                px="4"
                py="3"
                textAlign="center"
              >
                <Text fontSize="sm" color="gray.600">
                  Total mensal estimado
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="#1B2559">
                  {fmt(newMonthlyTotal)}
                </Text>
              </Box>

              {carteiraData?.saldos && (
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  bg="blue.50"
                  border="1px"
                  borderColor="blue.100"
                  rounded="lg"
                  px="4"
                  py="3"
                  fontSize="sm"
                  color="blue.800"
                >
                  <Flex align="center" gap="2">
                    <Icon
                      as={MdAccountBalanceWallet}
                      w="4"
                      h="4"
                      flexShrink="0"
                    />
                    <Text>
                      Seu saldo disponível:{" "}
                      <Text as="strong">
                        {`R$ ${getWalletBalanceFormatted(carteiraData?.saldos)?.toFixed(2).replace(".", ",") || "0,00"}`}
                      </Text>
                    </Text>
                  </Flex>

                  <Flex gap="2">
                    <ModalAdicionarSaldoStorm
                      updateTransacoes={async () => onUpdate()}
                    />
                    <ModalAdicionarSaldoPIX
                      updateTransacoes={async () => onUpdate()}
                    />
                    <Button
                      color="#141B3B"
                      bg="#fff"
                      _hover={{ bg: "rgba(255,255,255,0.9)" }}
                      _active={{ bg: "rgba(255,255,255,0.8)" }}
                      onClick={() => openModalAdicionarSaldoPIX()}
                      leftIcon={<PIXIcon width="14px" height="14px" />}
                      size="xs"
                    >
                      Depósito PIX
                    </Button>
                    {user.userData.type !== "PARCEIRO_GERENTE_VAREJO" &&
                      user.userData.parentCustomerUserType !==
                        "PARCEIRO_GERENTE_VAREJO" && (
                        <Button
                          color="#141B3B"
                          bg="#fff"
                          _hover={{ bg: "rgba(255,255,255,0.9)" }}
                          _active={{ bg: "rgba(255,255,255,0.8)" }}
                          onClick={() => openModalAdicionarSaldoStorm()}
                          leftIcon={<CashIcon width="15px" height="15px" />}
                          size="xs"
                        >
                          C/C Grupo Nova
                        </Button>
                      )}
                  </Flex>
                </Flex>
              )}

              {/* Actions */}
              <Flex direction={{ base: "column", sm: "row" }} gap="3" pt="2">
                <Button
                  flex="1"
                  py="3"
                  isLoading={loading}
                  onClick={handleConfirm}
                >
                  Confirmar e Pagar Licença
                </Button>
              </Flex>
            </VStack>
          </Box>
        </Box>
      </Box>
    </>
  );
}
